import { Bar } from "react-chartjs-2"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons"
import { Grid, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from "chart.js"
import { DeliveryType, TimePeriodMeetingService } from "../../production/meetingService/meetingServiceTypes"
import { DashboardDataCard, DashboardDataContents } from "../../../../dashboards/components/DashboardDataCard"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { MerchantHomeQuery, fetchMeetingServiceLimit, fetchMeetingServiceOrdersPerHour } from "../merchantHomeApi"
import { LocalizedStrict, LocalizedEnum } from "../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../localization/useDateTime"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { faUserGroup } from "@fortawesome/pro-light-svg-icons"
import { OrderPackageSlip } from "../../production/meetingService/components/OrderPackageSlip"
import { useState } from "react"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, ChartTooltip, Legend)

const useStyles = makeStyles()((theme) => ({
    container: {
        gap: "1.75rem",
    },
    card: {
        width: "37rem",
        height: "auto",
    },
    graphWrapper: {
        width: "100%",
        height: "100%",
    },
    noSpacer: {
        height: 0,
    },

    tableHeadTextColor: {
        color: theme.palette.tailwind.slate[500],
    },
    tableRow: {
        "& > td": {
            color: theme.palette.tailwind.slate[900],
        },
    },
    titleButton: {
        cursor: "pointer",
        color: "#2196F3",
        marginRight: "-0.75rem",
    },
    accentColor: {
        color: theme.palette.tailwind.slate[500],
    },
    badge: {
        "& .MuiBadge-badge": {
            background: theme.palette.common.black,
            color: theme.palette.common.white,
            right: "-0.5rem",
            minWidth: "1rem",
            minHeight: "1rem",
        },
    },
    badgeIcon: {
        color: "#7bf1a8",
        fontSize: "1rem",
    },
    secondaryLine: {
        color: theme.palette.tailwind.slate[500],
    },
    cellMainText: {
        color: theme.palette.tailwind.slate[700],
    },
    cellTime: {
        color: theme.palette.tailwind.slate[900],
        fontSize: "0.875rem",
        width: "6.875rem",
    },
    deliveryLocation: {
        paddingLeft: "1.25rem",
        paddingRight: "1.25rem",
    },
    emptyState: {
        paddingTop: "1rem",
        paddingBottom: "1rem",
    },
    subtitleNoWrap: {
        display: "flex",
        alignItems: "center",
    },
}))

export function MeetingServiceInfoDeliveryPlanning() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()

    const { data } = useApiQuery(fetchMeetingServiceOrdersPerHour, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_ORDERS_PER_HOURS,
        dependencies: {
            merchantId,
            period: TimePeriodMeetingService.TODAY,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const labels = data
        ? data.meetingServiceSale.salesPerHour.map((item) => {
              const fromTime = item.period.from.time.substring(0, 2)
              const toTime = item.period.to.time.substring(0, 2)
              return `${fromTime} - ${toTime}`
          })
        : []

    const chartData = {
        labels,
        datasets: [
            {
                label: l10n.getString("merchant-home-meeting-service-delivery-planning-delivery"),
                data: data
                    ? data.meetingServiceSale.salesPerHour.map(
                          (item) =>
                              item.deliveryTypes?.find((type) => type.name === DeliveryType.DELIVERY)?.salesSummary
                                  ?.numberOfOrders || 0,
                      )
                    : 0,
                backgroundColor: "#ffb1c1",
                borderColor: "#ff6484",
                borderWidth: 2,
                borderRadius: 0,
                borderSkipped: false,
            },
            {
                label: l10n.getString("merchant-home-meeting-service-delivery-planning-picked-up"),
                data: data
                    ? data.meetingServiceSale.salesPerHour.map(
                          (item) =>
                              item.deliveryTypes?.find((type) => type.name === DeliveryType.DELIVERY)?.salesSummary
                                  ?.numberOfOrders || 0,
                      )
                    : 0,
                backgroundColor: "#9bd0f5",
                borderColor: "#36a2eb",
                borderWidth: 2,
                borderRadius: 0,
                borderSkipped: false,
            },
            {
                label: l10n.getString("merchant-home-meeting-service-delivery-planning-eating-in-canteen"),
                data: data
                    ? data.meetingServiceSale.salesPerHour.map(
                          (item) =>
                              item.deliveryTypes?.find((type) => type.name === DeliveryType.EATING_IN_CANTEEN)
                                  ?.salesSummary?.numberOfOrders || 0,
                      )
                    : 0,
                backgroundColor: "#a4dfdf",
                borderColor: "#4bc0c0",
                borderWidth: 2,
                borderRadius: 0,
                borderSkipped: false,
            },
        ],
    }

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "bottom" as const,
            },
            title: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                stepSize: 10,
            },
        },
    }

    return (
        <Grid container>
            <Bar data={chartData} options={options} />
        </Grid>
    )
}

const NUMBER_OF_ORDERS = 5

export function MeetingServiceInfoNextDeliveries() {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const [showPackSlipModal, setShowPackSlipModal] = useState(false)
    const [selectedOrderId, setSelectedOrderId] = useState<number | undefined>()

    const { data } = useApiQuery(fetchMeetingServiceLimit, {
        queryName: MerchantHomeQuery.MEETING_SERVICE_LIMIT,
        dependencies: {
            merchantId,
            limit: NUMBER_OF_ORDERS,
        },
        suspense: true,
        useErrorBoundary: true,
        refetchInterval: 30000,
    })

    const handleRowClick = (orderId: number) => {
        setSelectedOrderId(orderId)
        setShowPackSlipModal(true)
    }

    const handleClosePackSlip = () => {
        setShowPackSlipModal(false)
        setSelectedOrderId(undefined)
    }

    if (!data?.orders?.length) {
        return (
            <Typography variant="body2" className={`${classes.tableHeadTextColor} ${classes.emptyState}`}>
                <LocalizedStrict id="merchant-home-meeting-service-no-upcoming-deliveries">
                    <>There are no upcoming deliveries.</>
                </LocalizedStrict>
            </Typography>
        )
    }

    return (
        <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.tableHeadTextColor}>
                            <LocalizedStrict id="merchant-home-meeting-service-table-delivery-column">
                                <>Delivery</>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell />
                        <TableCell className={classes.tableHeadTextColor}>
                            <LocalizedStrict id="merchant-home-meeting-service-table-meeting-column">
                                <>Meeting</>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.orders.map((order) => {
                        const rawDeliveryType = order.deliveryDetails.type

                        return (
                            <TableRow
                                key={order.id}
                                className={classes.tableRow}
                                onClick={() => handleRowClick(order.id)}
                                sx={{ cursor: "pointer" }}
                            >
                                <TableCell className={classes.cellTime}>
                                    {dateTimeFactory.fromApi(order.deliveryDetails.time).toFormat("d/M HH:mm")}
                                </TableCell>
                                <TableCell className={classes.deliveryLocation}>
                                    <Stack gap="0.5rem">
                                        <Typography variant="body2" className={classes.cellMainText}>
                                            <LocalizedEnum
                                                base="delivery-type-filter"
                                                enumValue={order.deliveryDetails.type}
                                            >
                                                <>{rawDeliveryType}</>
                                            </LocalizedEnum>
                                        </Typography>
                                        {rawDeliveryType !== DeliveryType.EATING_IN_CANTEEN &&
                                            order.deliveryDetails.location.name && (
                                                <Typography variant="caption" className={classes.secondaryLine}>
                                                    {order.deliveryDetails.location.name}
                                                </Typography>
                                            )}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <StandardTwoLinesCell
                                        title={order.meetingDetails?.meetingName}
                                        subtitle1={order.customer.displayName}
                                        subtitle2={
                                            <Stack direction="row" gap="0.25rem" alignItems="center" flexWrap="nowrap">
                                                <FontAwesomeIcon icon={faUserGroup} />
                                                {order.deliveryDetails.sittings}
                                            </Stack>
                                        }
                                        subtitleClassName={classes.subtitleNoWrap}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <FontAwesomeIcon icon={faChevronRight} className={classes.titleButton} />
                                </TableCell>
                            </TableRow>
                        )
                    })}
                </TableBody>
            </Table>
            {showPackSlipModal && !!selectedOrderId && (
                <OrderPackageSlip
                    orders={data?.orders ?? []}
                    onClose={handleClosePackSlip}
                    merchantId={merchantId}
                    id={selectedOrderId}
                    open={showPackSlipModal}
                />
            )}
        </>
    )
}

export function MeetingServiceInfo() {
    const { classes } = useStyles()

    return (
        <Grid container flexWrap="nowrap" alignItems="flex-start" className={classes.container}>
            {/* Delivery planning - hidden until we can make it work */}
            {false && (
                <LocalizedStrict
                    id="merchant-home-meeting-service-delivery-planning-card"
                    attrs={{ title: true, titleTooltip: true }}
                >
                    <DashboardDataCard
                        title="Delivery planning"
                        titleTooltip="Shows the distribution of meeting service orders during today"
                        slotProps={{
                            card: { className: classes.card },
                            graphWrapper: { className: classes.graphWrapper },
                        }}
                    >
                        <DashboardDataContents>
                            <MeetingServiceInfoDeliveryPlanning />
                        </DashboardDataContents>
                    </DashboardDataCard>
                </LocalizedStrict>
            )}
            <LocalizedStrict
                id="merchant-home-meeting-service-next-deliveries-card"
                attrs={{ title: true, titleTooltip: true }}
                vars={{ numberOfOrders: NUMBER_OF_ORDERS }}
            >
                <DashboardDataCard
                    title={`Next ${NUMBER_OF_ORDERS} deliveries`}
                    titleTooltip="The next 5 deliveries for today"
                    slotProps={{
                        card: { className: classes.card },
                        graphWrapper: { className: classes.graphWrapper },
                        spacer: { className: classes.noSpacer },
                    }}
                >
                    <DashboardDataContents>
                        <MeetingServiceInfoNextDeliveries />
                    </DashboardDataContents>
                </DashboardDataCard>
            </LocalizedStrict>
        </Grid>
    )
}
